import DOMPurify from 'dompurify';
import HTMLReactParser from 'html-react-parser';

import { SafeHtmlProps } from './safeHtml.types';

const SHOP_RICH_TEXT_SANITIZE_CONFIG = {
  ALLOW_ARIA_ATTR: false,
  ALLOW_DATA_ATTR: false,
  ALLOWED_ATTR: ['href'],
  ALLOWED_TAGS: [
    'a',
    'blockquote',
    'br',
    'em',
    'h2',
    'h3',
    'h4',
    'li',
    'ol',
    'p',
    'strong',
    'u',
    'ul'
  ],
  ALLOWED_URI_REGEXP: /^(?:https:\/\/|mailto:)/i,
  FORBID_ATTR: ['ping', 'srcset', 'style']
};

// DOMPurify v3 checks `target` against ALLOWED_URI_REGEXP — `_blank` fails the
// regexp and gets stripped even when listed in ALLOWED_ATTR. Pre-parse to record
// which anchor INDICES had target="_blank", then restore only those after
// sanitization. Index-keyed (not href-keyed) so two links to the same URL with
// different targets are handled independently.
const sanitizeShopRichText = (html: string): string => {
  const pre = new DOMParser().parseFromString(html, 'text/html');
  const blankIndices = new Set<number>();
  pre.querySelectorAll('a').forEach((a, i) => {
    if (a.getAttribute('target') === '_blank') blankIndices.add(i);
  });
  const sanitized = DOMPurify.sanitize(html, SHOP_RICH_TEXT_SANITIZE_CONFIG);
  if (!blankIndices.size) return sanitized;
  const post = new DOMParser().parseFromString(sanitized, 'text/html');
  post.querySelectorAll('a').forEach((a, i) => {
    if (blankIndices.has(i)) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    }
  });
  return post.body.innerHTML;
};

const SafeHtml: React.FC<SafeHtmlProps> = ({ html, policy = 'default' }) => {
  const sanitized =
    policy === 'shopRichText' ? sanitizeShopRichText(html) : DOMPurify.sanitize(html);
  // eslint-disable-next-line no-restricted-syntax
  const parsed = HTMLReactParser(sanitized);

  return <>{parsed}</>;
};

export default SafeHtml;
